/**
 * sifirlama.tsx
 *
 * NE İŞE YARAR : Yeni parola belirleme ekranı — `AU-05`i çağırır
 * KAYNAK SPEC  : 15-arayuz-standardi.md §15 (parola sıfırlama) · §10 (form kuralları)
 *                04-api-sozlesmesi.md AU-05 · ADR-019 §3 (parola politikası)
 *                ADR-008 §8 (sıfırlama tüm oturumları iptal eder)
 * BAĞLI OLDUĞU : src/api/istemci.ts · src/oturum/depo.ts · src/sabit/kimlik.ts
 * KULLANAN     : src/uygulama.tsx — `/parola/sifirlama` yolu
 * DİKKAT       : 1) JETON ADRESTEN OKUNUR (`?jeton=...`). Ad Türkçedir çünkü bağlantıyı
 *                   kurumun kendi süreci üretecek (AK-16) ve o süreç bu adı kullanır.
 *                   Jeton yoksa ekran **açıkça** söyler; boş bir formla karşılamak,
 *                   kullanıcının parolayı yazıp reddedilmesiyle biterdi.
 *                2) ASGARİ UZUNLUK BURADA DA DENETLENİR AMA KARAR SUNUCUNUNDUR
 *                   (ADR-019 §3). İstemci denetimi yalnız kullanıcıyı boşuna bir gidiş
 *                   dönüşten kurtarır; sızmış parola ve son 5 parola kontrolleri zaten
 *                   yalnız sunucuda yapılabilir.
 *                3) POLİTİKA İHLALLERİ `details.ihlaller`DEN OKUNUR (`04` AU-05 → `422`).
 *                   Sunucu dört ayrı sebep döndürüyor (`PAROLA_KISA`, `PAROLA_SIZMIS`,
 *                   `PAROLA_GECMISTE`, `PAROLA_KONTROL_EDILEMEDI`) ve kullanıcı hangisi
 *                   olduğunu bilmeden parolayı **rastgele** değiştirmeye çalışırdı.
 *                   Genel *"geçerli değil"* mesajı burada yetmez.
 *                4) `400 MALFORMED_REQUEST` BURADA *"BAĞLANTI GEÇERSİZ"*TİR. `04` AU-05
 *                   süresi geçmiş, kullanılmış ve hiç var olmamış jetonu **ayırmadan**
 *                   `400` döndürüyor; ekran da ayırmaz. Sunucunun genel mesajı
 *                   (*"İstek okunamadı"*) bu bağlamda yanlış olurdu — `hata.ts` notu 2'nin
 *                   izin verdiği ikinci istisna.
 *                5) SIFIRLAMA CİHAZLARI DA İPTAL EDER (`04` AU-05 · ADR-019 §5). Bu
 *                   yüzden yerel cihaz sırrı silinir: sunucuda karşılığı kalmayan bir
 *                   sırrı göndermeye devam etmek, her girişte sessizce boşa giden bir
 *                   adımdır. Kullanıcıya da söylenir — 2FA'nın neden geri geldiğini
 *                   bilmesi gerekir.
 *                6) İKİ PAROLA ALANI KARŞILAŞTIRILIR. Tek alan olsaydı yazım hatası
 *                   ancak bir sonraki girişte anlaşılır ve kullanıcı hesabından olurdu.
 * SON GÜNCELLEME: 2026-08-31 04:00:00
 */
import { Alert, Anchor, Button, List, PasswordInput, Stack, Text } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ApiHatasi } from '../../api/hata';
import { istek } from '../../api/istemci';
import { metin, type MetinAnahtari } from '../../metin';
import { cihazSirriniSil, oturumuSil } from '../../oturum/depo';
import { PAROLA_ASGARI_UZUNLUK } from '../../sabit/kimlik';
import { HataUyarisi } from '../ortak/hata-uyarisi';
import { KimlikDuzeni } from './duzen';

/** `04` AU-05'in `details.ihlaller` kodları → ekran metinleri (başlıktaki not 3). */
const IHLAL_METINLERI: Readonly<Record<string, MetinAnahtari>> = {
  PAROLA_KISA: 'parola.kisa',
  PAROLA_SIZMIS: 'parola.sizmis',
  PAROLA_GECMISTE: 'parola.gecmiste',
  PAROLA_KONTROL_EDILEMEDI: 'parola.kontrolEdilemedi',
};

export function SifirlamaEkrani(): ReactElement {
  const [adresParametreleri] = useSearchParams();
  const jeton = adresParametreleri.get('jeton') ?? '';

  const [parola, setParola] = useState('');
  const [parolaTekrar, setParolaTekrar] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [tamam, setTamam] = useState(false);
  const [hataMesaji, setHataMesaji] = useState<string | null>(null);
  const [ihlaller, setIhlaller] = useState<readonly string[]>([]);

  const girisBaglantisi = (
    <Anchor component={Link} to="/giris" size="sm">
      {metin('sifirlamaTalebi.girisEkranina')}
    </Anchor>
  );

  // Başlıktaki not 1 — jeton yoksa form hiç gösterilmez.
  if (jeton === '') {
    return (
      <KimlikDuzeni baslik={metin('sifirlama.baslik')} altBilgi={girisBaglantisi}>
        <HataUyarisi mesaj={metin('sifirlama.jetonYok')} />
      </KimlikDuzeni>
    );
  }

  if (tamam) {
    return (
      <KimlikDuzeni baslik={metin('sifirlama.tamam')} altBilgi={girisBaglantisi}>
        <Alert color="basari" variant="light" icon={<IconCircleCheck size={18} />} radius="sm">
          {/* Başlıktaki not 5 — 2FA'nın neden geri geldiği burada söyleniyor. */}
          <Text size="sm">{metin('sifirlama.tamamMetin')}</Text>
        </Alert>
      </KimlikDuzeni>
    );
  }

  async function gonder(olay: FormEvent<HTMLFormElement>): Promise<void> {
    olay.preventDefault();
    if (gonderiliyor) {
      return;
    }

    setHataMesaji(null);
    setIhlaller([]);

    if (parola !== parolaTekrar) {
      setHataMesaji(metin('sifirlama.eslesmiyor'));
      return;
    }
    if (parola.length < PAROLA_ASGARI_UZUNLUK) {
      // Başlıktaki not 2 — erken uyarı; karar yine sunucunundur.
      setHataMesaji(metin('parola.kisa'));
      return;
    }

    setGonderiliyor(true);
    try {
      await istek({
        yol: '/auth/password/reset',
        yontem: 'POST',
        govde: { token: jeton, new_password: parola },
      });

      // Başlıktaki not 5 — sunucu bütün oturumları ve cihazları iptal etti; yerel
      // kopyaları da temizlenir.
      oturumuSil();
      cihazSirriniSil();
      setTamam(true);
    } catch (hata) {
      if (hata instanceof ApiHatasi && hata.kod === 'VALIDATION_FAILED') {
        setIhlaller(hata.ihlaller);
        setHataMesaji(hata.ihlaller.length === 0 ? hata.message : null);
      } else if (hata instanceof ApiHatasi && hata.kod === 'MALFORMED_REQUEST') {
        setHataMesaji(metin('sifirlama.jetonGecersiz')); // Başlıktaki not 4.
      } else {
        setHataMesaji(hata instanceof ApiHatasi ? hata.message : metin('hata.beklenmeyen'));
      }
    } finally {
      setGonderiliyor(false);
    }
  }

  return (
    <KimlikDuzeni
      baslik={metin('sifirlama.baslik')}
      aciklama={metin('sifirlama.aciklama')}
      altBilgi={girisBaglantisi}
    >
      <form onSubmit={(olay) => void gonder(olay)} noValidate>
        <Stack gap="md">
          <HataUyarisi mesaj={hataMesaji} />

          {ihlaller.length === 0 ? null : (
            <Alert color="hata" variant="light" role="alert" radius="sm">
              <List size="sm" spacing="xs">
                {ihlaller.map((kod) => (
                  <List.Item key={kod}>
                    {IHLAL_METINLERI[kod] === undefined
                      ? metin('hata.beklenmeyen')
                      : metin(IHLAL_METINLERI[kod])}
                  </List.Item>
                ))}
              </List>
            </Alert>
          )}

          <PasswordInput
            label={metin('sifirlama.yeniParola')}
            value={parola}
            onChange={(olay) => setParola(olay.currentTarget.value)}
            autoComplete="new-password"
            required
          />

          {/* Başlıktaki not 6 — tek alan, yazım hatasını bir sonraki girişe erteler. */}
          <PasswordInput
            label={metin('sifirlama.yeniParolaTekrar')}
            value={parolaTekrar}
            onChange={(olay) => setParolaTekrar(olay.currentTarget.value)}
            autoComplete="new-password"
            required
          />

          <Button type="submit" loading={gonderiliyor} fullWidth>
            {metin('sifirlama.gonder')}
          </Button>
        </Stack>
      </form>
    </KimlikDuzeni>
  );
}
